// Where the registry's bodies are, from where the ship is.
//
// This drew one flat-coloured sphere per row at its own heliocentric position -
// the sim's stand-in for a solar system. The world the ship flies in is the
// Atlas's now, mounted at boot under the same root and drawn from the chase
// camera's pose, so nothing here is drawn. What is left is the one pass over
// the registry the tick needs: the pulling set, the body the ship is at, and a
// row's own heliocentric place and velocity for the land verb and the pilot's
// guidance to aim at. The epoch it is placed at is the shared clock's, so this
// pass runs every frame rather than short-circuiting after boot.

import type { WorldVec } from '../world/frame';
import { heliocentric } from '../world/ephemeris.ts';
import type { BodyRow } from '../world/types';

export interface BodyFocus {
  readonly row: BodyRow;
  /** Metres from the ship to the body's centre. */
  readonly distanceM: number;
  /** Half the angle the disc subtends, radians. */
  readonly apparentRadius: number;
}

/** One row and the heliocentric metres it was last placed at. */
export interface BodyPlace {
  readonly row: BodyRow;
  /** Mutated in place by `update`, so a per-tick reader allocates nothing. */
  readonly world: WorldVec;
  /**
   * The row's own heliocentric velocity at that instant, metres per second.
   * `heliocentric()` composes it beside the position and this used to throw it
   * away; the guidance rows read it, so CLOSING and the two error angles mean
   * relative to a body that is moving.
   */
  readonly velocity: WorldVec;
}

export interface BodyPlaces {
  /**
   * Every row and where it sits, for anything that has to sweep the registry
   * per tick - the ship's own pulling set does. Built once; the vectors in it
   * are the same ones `update` writes, so a sweep of it costs one pass.
   */
  readonly places: readonly BodyPlace[];
  update(jdTdb: number, shipWorld: WorldVec): void;
  /** The body the ship is at: the largest disc from where it sits. */
  focus(): BodyFocus | null;
  /** A row's own heliocentric place at the last update, for anything aiming at it. */
  worldAt(id: string): WorldVec | null;
  /** A row's own heliocentric velocity at the last update, metres per second. */
  velocityAt(id: string): WorldVec | null;
  dispose(): void;
}

export interface BodyPlacesConfig {
  rows: readonly BodyRow[];
  /**
   * `?landlabelunlatched=1`: the pre-fix rule, largest disc wins with no
   * hysteresis. Phobos's own hold radius against Mars sits under its own
   * surface, so near Phobos the two discs read within a fraction of a
   * percent of each other and this reading flips every frame.
   * `descent-dojo.mjs --inject land-label-unlatched` reaches it; nothing a
   * player presses does.
   */
  unlatched?: boolean;
}

interface Body {
  row: BodyRow;
  world: WorldVec;
  velocity: WorldVec;
}

/**
 * How much bigger a challenger's disc has to read before it is even a
 * candidate to take the focus from the body already named. At the Mars/
 * Phobos crossover the two discs' apparent radii can differ by well under a
 * percent from one frame to the next - ship jitter, not a real close-in - so
 * a plain "largest wins" compare flips there every frame. Ten percent clears
 * that jitter with room to spare.
 */
const FOCUS_HYSTERESIS_RATIO = 1.1;

/**
 * How many consecutive `update()` calls a challenger has to keep clearing the
 * margin before the button actually lets go of the incumbent. `update()` runs
 * once per rendered frame in ordinary flight, but a warp skip or a guidance
 * burn packs several sim ticks into one rendered frame, each its own call - so
 * this is ticks, not seconds. A full-throttle burn away from Phobos and
 * straight back over `descent-dojo.mjs --ship --ship-orbit phobos-low`
 * measures 15 ticks past the margin before falling back under it; twice that
 * lets an excursion that never settles read as a change while a swing that
 * reverses on its own within about half a second at 60 fps never does.
 */
const FOCUS_HOLD_TICKS = 30;

export function createBodyPlaces(config: BodyPlacesConfig): BodyPlaces {
  const bodies: Body[] = [];
  const byId = new Map<string, Body>();
  for (const row of config.rows) {
    if (!(row.radius_m > 0)) continue;
    const b: Body = { row, world: { x: 0, y: 0, z: 0 }, velocity: { x: 0, y: 0, z: 0 } };
    bodies.push(b);
    byId.set(row.id, b);
  }
  // Reused every `update()` call: `heliocentric`'s own out-parameter, so
  // placing every row per tick costs no allocation beyond this one pair of
  // vectors, held for the sweep's own lifetime rather than the tick's.
  const scratch = { position: { x: 0, y: 0, z: 0 }, velocity: { x: 0, y: 0, z: 0 } };
  let current: BodyFocus | null = null;
  let placedAt = Number.NaN;
  const unlatched = !!config.unlatched;
  let challengerId: string | null = null;
  let challengerStreak = 0;

  return {
    places: bodies.map((b) => ({ row: b.row, world: b.world, velocity: b.velocity })),
    update(jdTdb: number, shipWorld: WorldVec): void {
      const replace = jdTdb !== placedAt;
      placedAt = jdTdb;
      let best: BodyFocus | null = null;
      let incumbent: BodyFocus | null = null;
      for (const b of bodies) {
        if (replace) {
          heliocentric(b.row, config.rows, jdTdb, scratch);
          b.world.x = scratch.position.x * 1000;
          b.world.y = scratch.position.y * 1000;
          b.world.z = scratch.position.z * 1000;
          b.velocity.x = scratch.velocity.x * 1000;
          b.velocity.y = scratch.velocity.y * 1000;
          b.velocity.z = scratch.velocity.z * 1000;
        }
        const d = Math.hypot(
          b.world.x - shipWorld.x, b.world.y - shipWorld.y, b.world.z - shipWorld.z);
        const apparent = d > b.row.radius_m ? Math.asin(b.row.radius_m / d) : Math.PI / 2;
        const candidate: BodyFocus = { row: b.row, distanceM: d, apparentRadius: apparent };
        if (!best || apparent > best.apparentRadius) best = candidate;
        if (current && b.row.id === current.row.id) incumbent = candidate;
      }
      // The one rule is "largest disc wins" (`best`); hysteresis only decides
      // whether a challenger that is not the body already named has won by
      // enough, and for long enough, to take it. `!incumbent` covers the body
      // already named having left the registry pass entirely (disposed, or
      // never a candidate).
      if (unlatched || !incumbent || !best) {
        current = best;
        challengerId = null;
        challengerStreak = 0;
        return;
      }
      if (best.row.id === incumbent.row.id
        || !(best.apparentRadius > incumbent.apparentRadius * FOCUS_HYSTERESIS_RATIO)) {
        // The incumbent is still winning, or nothing has cleared the margin
        // against it: no challenge is in progress.
        challengerId = null;
        challengerStreak = 0;
        current = incumbent;
        return;
      }
      challengerStreak = best.row.id === challengerId ? challengerStreak + 1 : 1;
      challengerId = best.row.id;
      if (challengerStreak >= FOCUS_HOLD_TICKS) {
        current = best;
        challengerId = null;
        challengerStreak = 0;
      } else {
        current = incumbent;
      }
    },
    focus(): BodyFocus | null { return current; },
    worldAt(id: string): WorldVec | null {
      return byId.get(id)?.world ?? null;
    },
    velocityAt(id: string): WorldVec | null {
      return byId.get(id)?.velocity ?? null;
    },
    dispose(): void {
      bodies.length = 0;
      byId.clear();
      current = null;
    },
  };
}
